import FileSaver from 'file-saver';
import { TFunction } from 'i18next';
import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import FormStore from '../core/stores/form-store';
import i18n from 'src/i18n';
import { CsvReportKeys } from '../common/types';
import { CsvReportKeys as IdentityReportKeys } from './users-store';

export enum LicenseLogType {
  LicenseCreation = 'LicenseCreation',
  LicenseUpdate = 'LicenseUpdate',
  LicenseSignedOff = 'LicenseSignedOff',
  LicenseRejected = 'LicenseRejected',
  CanCreateUnlimitedLicense = 'CanCreateUnlimitedLicense',
  ErrorCreatingLicense = 'ErrorCreatingLicense',
  ErrorUpdatingLicense = 'ErrorUpdatingLicense',
  ErrorCreatingLicenseMaximumLicenseDueDate = 'ErrorCreatingLicenseMaximumLicenseDueDate',
  ErrorCreatingLicenseMaxLicensesPeriodInDays = 'ErrorCreatingLicenseMaxLicensesPeriodInDays',
  ErrorCreatingLicenseMaxLicenseNumber = 'ErrorCreatingLicenseMaxLicenseNumber',
  ErrorCreatingLicenseAllowedDevices = 'ErrorCreatingLicenseAllowedDevices',
  ErrorUpdatingLicenseMaximumLicenseDueDate = 'ErrorUpdatingLicenseMaximumLicenseDueDate',
  ErrorUpdatingLicenseMaxLicensesPeriodInDays = 'ErrorUpdatingLicenseMaxLicensesPeriodInDays',
  ErrorUpdatingLicenseAllowedDevices = 'ErrorUpdatingLicenseAllowedDevices',
}

export enum StructmasterLicenseLogType {
  UnsignedLicenseCreation = 'UnsignedLicenseCreation',
  SignedLicenseCreation = 'SignedLicenseCreation',
  UnsignedLicenseUpdate = 'UnsignedLicenseUpdate',
  SignedLicenseUpdate = 'SignedLicenseUpdate',
  ErrorCreatingUnsignedLicense = 'ErrorCreatingUnsignedLicense',
  ErrorCreatingSignedLicense = 'ErrorCreatingSignedLicense',
  ErrorUpdatingUnsignedLicense = 'ErrorUpdatingUnsignedLicense',
  ErrorUpdatingSignedLicense = 'ErrorUpdatingSignedLicense',
  LicenseSignedOff = 'LicenseSignedOff',
  DeleteUnsignedLicense = 'DeleteUnsignedLicense',
  DeleteSignedLicense = 'DeleteSignedLicense',
}

export interface StructmasterLicenseLogSummary {
  name: string;
  id: string;
  structMasterLicenseId: string;
  userName: string;
  createdOn: string;
  description: string;
  logType: StructmasterLicenseLogType;
  organizationId?: string;
  organizationName?: string;
  userId?: string;
}

export interface LicenseLogSummary {
  id: string;
  identityId: string;
  organizationId: string;
  licenseDataId: string;
  licenseCreatedByName: string;
  logType: LicenseLogType;
  userName: string;
  createdOn: string;
  organizationName: string;
  toolCode: string;
  deviceType: string;
  fromDate: string;
  untilDate: string;
  unlimited: string;
  newToolCode: string;
  newDeviceType: string;
  newFromDate: string;
  newUntilDate: string;
  newUnlimited: string;
  description: string;
  project: string;
  customer: string;
  newProject: string;
  newCustomer: string;
}

export interface LicenseLogSummaryCsvRequest {
  createdOn: string;
  logType: string;
  userName: string;
  description: string;
}

export interface StrutcMasterLicenseLogSummaryCsvRequest extends LicenseLogSummaryCsvRequest {
  organization: string;
  licenseName: string;
}

export interface LicenseLogReportHeader {
  key: CsvReportKeys;
}

export interface LicenseLogCsvReportRequest {
  csvSeparator: string;

  headers: LicenseLogReportHeader[];

  currentLanguage: string;

  fileName: string;

  items: LicenseLogSummaryCsvRequest[];
}

export interface StructmasterLicenseLogReportHeader {
  key: IdentityReportKeys;
}

export interface StructmasterLicenseLogCsvReportRequest {
  csvSeparator: string;

  headers: StructmasterLicenseLogReportHeader[];

  currentLanguage: string;

  fileName: string;

  items: StrutcMasterLicenseLogSummaryCsvRequest[];
}

export const getLicenseLogTranslatedHeader = (key: CsvReportKeys, t: TFunction) => {
  switch (key) {
    case CsvReportKeys.CreatedOn:
      return t('CreatedOn');
    case CsvReportKeys.LogType:
      return t('LogType');
    case CsvReportKeys.UserName:
      return t('UserName');
    case CsvReportKeys.Description:
      return t('Description');
  }
};

export const getStructmasterLicenseLogTranslatedHeader = (key: IdentityReportKeys, t: TFunction) => {
  switch (key) {
    case IdentityReportKeys.CreatedOn:
      return t('CreatedOn');
    case IdentityReportKeys.LogType:
      return t('LogType');
    case IdentityReportKeys.UserName:
      return t('UserName');
    case IdentityReportKeys.Description:
      return t('Description');
    case IdentityReportKeys.Organization:
      return t('Organization');
    case IdentityReportKeys.LicenseName:
      return t('LicenseName');
  }
};

export const getLicenseLogTableHeaders = (t: TFunction) => {
  return [
    {
      key: CsvReportKeys.CreatedOn,
      translatedHeader: getLicenseLogTranslatedHeader(CsvReportKeys.CreatedOn, t),
    },
    {
      key: CsvReportKeys.LogType,
      translatedHeader: getLicenseLogTranslatedHeader(CsvReportKeys.LogType, t),
    },
    {
      key: CsvReportKeys.UserName,
      translatedHeader: getLicenseLogTranslatedHeader(CsvReportKeys.UserName, t),
    },
    {
      key: CsvReportKeys.Description,
      translatedHeader: getLicenseLogTranslatedHeader(CsvReportKeys.Description, t),
    },
  ];
};

export const getStructmasterLicenseLogsTableHeaders = (t: TFunction) => {
  return [
    {
      key: IdentityReportKeys.LicenseName,
      translatedHeader: getStructmasterLicenseLogTranslatedHeader(IdentityReportKeys.LicenseName, t),
    },
    {
      key: IdentityReportKeys.Organization,
      translatedHeader: getStructmasterLicenseLogTranslatedHeader(IdentityReportKeys.Organization, t),
    },
    {
      key: IdentityReportKeys.CreatedOn,
      translatedHeader: getStructmasterLicenseLogTranslatedHeader(IdentityReportKeys.CreatedOn, t),
    },
    {
      key: IdentityReportKeys.LogType,
      translatedHeader: getStructmasterLicenseLogTranslatedHeader(IdentityReportKeys.LogType, t),
    },
    {
      key: IdentityReportKeys.UserName,
      translatedHeader: getStructmasterLicenseLogTranslatedHeader(IdentityReportKeys.UserName, t),
    },
    {
      key: IdentityReportKeys.Description,
      translatedHeader: getStructmasterLicenseLogTranslatedHeader(IdentityReportKeys.Description, t),
    },
  ];
};

export class LicenseLogSummaryStore extends DataStore<LicenseLogSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/licenses/get-logs`, []);
  }
}

export class LicenseLogReportItemStore extends FormStore<any, any> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/licenses`);
  }

  public async get(item: LicenseLogCsvReportRequest) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<any, Blob>(`${this.baseUrl}/export-logs`, item, { responseType: 'blob' });

      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }

      FileSaver.saveAs(result.data, item.fileName);

      return true;
    });
  }
}

export class StructmasterLicenseLogsStore extends DataStore<StructmasterLicenseLogSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/structmaster-license-logs`, []);
  }

  public async load(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<StructmasterLicenseLogSummary>>(
        `${this.baseUrl}/get-logs?${DataStore.buildUrl(query as Query)}`
      );
      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }

  public async export(item: StructmasterLicenseLogCsvReportRequest) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<any, Blob>(`${this.baseUrl}/export-logs`, item, { responseType: 'blob' });

      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }

      FileSaver.saveAs(result.data, item.fileName);

      return true;
    });
  }
}
